import React from 'react';

import { useAppDispatch, useAppSelector, type AppDispatch, type RootState } from '@/store';
import {
  blogFetchNotices,
  blogSettleAutopilotCredits,
  selectBlogAutopilotPrice,
  selectBlogCreditsBalance,
  selectBlogCreditsNoticeNeeded,
} from '@/store/slices/blog';
import { fetchOwnerWallets } from '@/store/slices/wallet';

import { autopilotSettleKey } from './autopilotCredits';

/**
 * Close the Autopilot credits notice once the owner's credits cover it, and
 * ask for the missed article soon rather than at the next slot
 * (`docs/auto-generation.md` in spark-blog, "Owner Studio behaviour").
 *
 * Two kinds of caller. The places that host a buy-credits sheet for the
 * notice itself (Home's card, the Website hub's card, MainLayout for the
 * email's `/account/home?credits=buy`) call it once the purchase has landed.
 * The layout's watcher (`useAutopilotCreditsSettle`) calls it whenever the
 * balance is seen to cover an open notice, which is what closes a notice the
 * owner topped up for from anywhere else: the header chip, the creatives page,
 * the editor. The service never hears of a top-up, so without this the notice
 * stayed open beside a balance that covered it, the blog page said "Paused",
 * and the missed article waited two days.
 *
 * The wallet is re-read first, which also refreshes the header chip: Autopilot
 * spends on the service, so the balance this tab holds can be higher than the
 * real one. A wallet that cannot be read decides nothing. A caller whose
 * purchase has just landed passes `walletFresh`: the sheet only reports success
 * once its own re-read saw the credits arrive, so that balance is handed to the
 * settle as is, and neither a second read nor another surface's read failing
 * meanwhile (the watcher reads the wallet on the same change) can void it. The
 * settle then re-reads the settings and the notice and decides. When
 * it closed the notice, the notices are read once more: a "Clear" pressed on
 * the new-articles card while it ran answered after the settle's own clear,
 * and the settle's answer must not bring that card back. All under the active
 * brand, which is the displayed one whenever this runs.
 */
export const settleAutopilotTopup = async (
  dispatch: AppDispatch,
  { walletFresh = false }: { walletFresh?: boolean } = {},
): Promise<void> => {
  let balance: number | null | undefined;
  if (walletFresh) {
    balance = dispatch((_dispatch: AppDispatch, getState: () => RootState) => getState().ownerWallet?.credits ?? null);
  } else {
    const wallet = await dispatch(fetchOwnerWallets());
    if (!fetchOwnerWallets.fulfilled.match(wallet)) return;
  }
  const settle = await dispatch(blogSettleAutopilotCredits(balance === undefined ? undefined : { balance }));
  if (blogSettleAutopilotCredits.fulfilled.match(settle) && settle.payload.outcome === 'settled') {
    void dispatch(blogFetchNotices());
  }
};

/**
 * The layout's watcher for an Autopilot credits notice the balance already
 * covers (see `settleAutopilotTopup`). It reads what other surfaces have
 * already loaded (the blog page's settings, the Spotlight's notices, the
 * header's wallet), and reads the notices itself only when the balance rises
 * while it knows of no open notice, so a studio at rest makes no request for
 * them.
 * It acts once per state (`autopilotSettleKey`): a settle that changed nothing
 * is not retried until a value it decided on changes, such as a new top-up.
 */
export const useAutopilotCreditsSettle = (): void => {
  const dispatch = useAppDispatch();
  const brandId = useAppSelector((state) => state.casinoInstance.activeBrand?.id);
  const displayBrandId = useAppSelector((state) => state.casinoInstance.displayBrand?.id);
  const needed = useAppSelector(selectBlogCreditsNoticeNeeded);
  const price = useAppSelector(selectBlogAutopilotPrice);
  const balance = useAppSelector(selectBlogCreditsBalance);
  const noticesLoading = useAppSelector((state) => state.blog.noticesLoading);
  const settledFor = React.useRef<string | null>(null);
  // The last balance this watcher trusted, on a settled brand.
  const lastBalance = React.useRef<number | null>(null);

  // A top-up the studio knows of no open notice for: none has been read (a
  // page other than Home, the Website hub or the Blog page, or right after a
  // brand switch), or the one read earlier said none was open and a run has
  // been refused since. The balance rose, so read the notices once and let the
  // key below decide. Only on a rise, so a studio at rest still makes no
  // request for them. The rise is measured from the last balance trusted on a
  // settled brand, so neither a failed wallet read in between nor a brand
  // switch under way can hide it.
  React.useEffect(() => {
    if (balance === null || brandId === undefined || brandId !== displayBrandId) return;
    const previous = lastBalance.current;
    lastBalance.current = balance;
    if (previous === null || balance <= previous || needed !== null || noticesLoading) return;
    void dispatch(blogFetchNotices());
  }, [dispatch, balance, brandId, displayBrandId, needed, noticesLoading]);

  const key = autopilotSettleKey({ brandId, displayBrandId, needed, balance, price });
  React.useEffect(() => {
    // Nothing to settle any more: a later refusal is a new notice, even one
    // that reads exactly like the last (same price, and a balance this tab
    // has not re-read since Autopilot spent it), and must be looked at again.
    if (key === null) {
      settledFor.current = null;
      return;
    }
    if (key === settledFor.current) return;
    settledFor.current = key;
    void settleAutopilotTopup(dispatch);
  }, [dispatch, key]);
};
